import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  AddExerciseToWorkoutInput,
  CreateWorkoutTemplateInput,
  DeleteSetInput,
  LogSetInput,
  ListWorkoutTemplatesInput,
  RemoveExerciseFromWorkoutInput,
  ReplaceWorkoutExerciseInput,
  ReorderWorkoutExercisesInput,
  ReplaceTemplateExercisesInput,
  StartWorkoutFromTemplateInput,
  UpdateSetInput,
  UpdateWorkoutTemplateInput,
  WorkoutRepository,
  WorkoutSeriesRange,
} from '../../application/ports/workout.repository';
import {
  InvalidDateRangeError,
  InvalidWorkoutInputError,
  NetworkError,
  ServerError,
  TemplateNotFoundError,
  UnauthorizedError,
  WorkoutAlreadyActiveError,
  WorkoutNotFoundError,
  type WorkoutError,
} from '../../domain/errors';
import type {
  CompleteWorkoutResult,
  LogSetResult,
  ReplaceWorkoutExerciseResult,
  Workout,
} from '../../domain/workout';
import type {WorkoutId} from '../../domain/workout-id.value-object';
import type {WorkoutSummary} from '../../domain/workout-summary';
import type {WorkoutTemplate, WorkoutTemplateSummary} from '../../domain/workout-template';
import type {WorkoutTemplateId} from '../../domain/workout-template-id.value-object';
import type {CompleteWorkoutDtoToResultMapper} from '../mappers/complete-workout-dto-to-result.mapper';
import type {LogSetDtoToLogSetResultMapper} from '../mappers/log-set-dto-to-log-set-result.mapper';
import type {ReplaceWorkoutExerciseDtoToResultMapper} from '../mappers/replace-workout-exercise-dto-to-result.mapper';
import type {WorkoutDtoToWorkoutMapper} from '../mappers/workout-dto-to-workout.mapper';
import type {WorkoutSummaryDtoToSummaryMapper} from '../mappers/workout-summary-dto-to-summary.mapper';
import type {WorkoutTemplateDtoToWorkoutTemplateMapper} from '../mappers/workout-template-dto-to-workout-template.mapper';
import type {WorkoutTemplateSummaryDtoToSummaryMapper} from '../mappers/workout-template-summary-dto-to-summary.mapper';

export class RpcWorkoutRepository implements WorkoutRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly templateMapper: WorkoutTemplateDtoToWorkoutTemplateMapper,
    private readonly summaryMapper: WorkoutTemplateSummaryDtoToSummaryMapper,
    private readonly workoutMapper: WorkoutDtoToWorkoutMapper,
    private readonly workoutSummaryMapper: WorkoutSummaryDtoToSummaryMapper,
    private readonly logSetMapper: LogSetDtoToLogSetResultMapper,
    private readonly completeMapper: CompleteWorkoutDtoToResultMapper,
    private readonly replaceMapper: ReplaceWorkoutExerciseDtoToResultMapper,
  ) {}

  async listTemplates(
    input: ListWorkoutTemplatesInput,
  ): Promise<Result<Page<WorkoutTemplateSummary>, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['workout-templates'].$get({
          query: {
            ...(input.cursor !== null && {cursor: input.cursor}),
            ...(input.q.length > 0 && {q: input.q}),
          },
        }),
      );

      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }

      return ok({
        items: dto.data.map(item => this.summaryMapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause, 'template');
    }
  }

  async getTemplate(templateId: WorkoutTemplateId): Promise<Result<WorkoutTemplate, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['workout-templates'][':templateId'].$get({
          param: {templateId},
        }),
      );
      return ok(this.templateMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'template');
    }
  }

  async createTemplate(
    input: CreateWorkoutTemplateInput,
  ): Promise<Result<WorkoutTemplate, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['workout-templates'].$post({
          json: {
            name: input.name,
            notes: input.notes,
            exercises: input.exercises.map(exercise => ({
              exerciseId: exercise.exerciseId,
              ...(exercise.orderIndex !== undefined && {orderIndex: exercise.orderIndex}),
              ...(exercise.groupId !== undefined && {groupId: exercise.groupId}),
              ...(exercise.targetSets !== undefined && {targetSets: exercise.targetSets}),
              ...(exercise.targetReps !== undefined && {targetReps: exercise.targetReps}),
              ...(exercise.targetWeightKg !== undefined && {
                targetWeightKg: exercise.targetWeightKg,
              }),
              ...(exercise.targetRestSeconds !== undefined && {
                targetRestSeconds: exercise.targetRestSeconds,
              }),
              ...(exercise.notes !== undefined && {notes: exercise.notes}),
            })),
          },
        }),
      );
      return ok(this.templateMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'template');
    }
  }

  async updateTemplate(
    input: UpdateWorkoutTemplateInput,
  ): Promise<Result<WorkoutTemplate, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['workout-templates'][':templateId'].$patch({
          param: {templateId: input.templateId},
          json: {
            ...(input.name !== undefined && {name: input.name}),
            ...(input.notes !== undefined && {notes: input.notes}),
          },
        }),
      );
      return ok(this.templateMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'template');
    }
  }

  async replaceTemplateExercises(
    input: ReplaceTemplateExercisesInput,
  ): Promise<Result<WorkoutTemplate, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['workout-templates'][':templateId'].exercises.$put({
          param: {templateId: input.templateId},
          json: {
            exercises: input.exercises.map(exercise => ({
              exerciseId: exercise.exerciseId,
              ...(exercise.orderIndex !== undefined && {orderIndex: exercise.orderIndex}),
              ...(exercise.groupId !== undefined && {groupId: exercise.groupId}),
              ...(exercise.targetSets !== undefined && {targetSets: exercise.targetSets}),
              ...(exercise.targetReps !== undefined && {targetReps: exercise.targetReps}),
              ...(exercise.targetWeightKg !== undefined && {
                targetWeightKg: exercise.targetWeightKg,
              }),
              ...(exercise.targetRestSeconds !== undefined && {
                targetRestSeconds: exercise.targetRestSeconds,
              }),
              ...(exercise.notes !== undefined && {notes: exercise.notes}),
            })),
          },
        }),
      );
      return ok(this.templateMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'template');
    }
  }

  async duplicateTemplate(
    templateId: WorkoutTemplateId,
  ): Promise<Result<WorkoutTemplate, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['workout-templates'][':templateId'].duplicate.$post({
          param: {templateId},
        }),
      );
      return ok(this.templateMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'template');
    }
  }

  async deleteTemplate(templateId: WorkoutTemplateId): Promise<Result<void, WorkoutError>> {
    try {
      await parseResponse(
        this.rpcClient['workout-templates'][':templateId'].$delete({
          param: {templateId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause, 'template');
    }
  }

  async getActiveWorkout(): Promise<Result<Workout | null, WorkoutError>> {
    try {
      const dto = await parseResponse(this.rpcClient.workouts.active.$get());
      if (dto === null || dto === undefined) {
        return ok(null);
      }
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async getWorkout(workoutId: WorkoutId): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].$get({
          param: {workoutId},
        }),
      );
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async getCompletedSeries(
    range: WorkoutSeriesRange,
  ): Promise<Result<readonly WorkoutSummary[], WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts.series.$get({
          query: {
            from: range.from,
            to: range.to,
          },
        }),
      );

      if ('error' in dto) {
        return err(new ServerError());
      }

      return ok(dto.data.map(item => this.workoutSummaryMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause, 'range');
    }
  }

  async startWorkoutFromTemplate(
    input: StartWorkoutFromTemplateInput,
  ): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts['from-template'].$post({
          json: {
            templateId: input.templateId,
          },
        }),
      );
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async discardWorkout(workoutId: WorkoutId): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].discard.$post({
          param: {workoutId},
        }),
      );
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async logSet(input: LogSetInput): Promise<Result<LogSetResult, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].exercises[':workoutExerciseId'].sets.$post({
          param: {
            workoutId: input.workoutId,
            workoutExerciseId: input.workoutExerciseId,
          },
          json: {
            ...(input.setId !== undefined && {setId: input.setId}),
            weightKg: input.weightKg,
            reps: input.reps,
            ...(input.rpe !== undefined && {rpe: input.rpe}),
            ...(input.isWarmup !== undefined && {isWarmup: input.isWarmup}),
            ...(input.isDropset !== undefined && {isDropset: input.isDropset}),
            ...(input.isFailure !== undefined && {isFailure: input.isFailure}),
            ...(input.notes !== undefined && {notes: input.notes}),
          },
        }),
      );
      return ok(this.logSetMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async updateSet(input: UpdateSetInput): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].exercises[':workoutExerciseId'].sets[':setId'].$patch(
          {
            param: {
              workoutId: input.workoutId,
              workoutExerciseId: input.workoutExerciseId,
              setId: input.setId,
            },
            json: {
              weightKg: input.weightKg,
              reps: input.reps,
              ...(input.rpe !== undefined && {rpe: input.rpe}),
              ...(input.isWarmup !== undefined && {isWarmup: input.isWarmup}),
              ...(input.isDropset !== undefined && {isDropset: input.isDropset}),
              ...(input.isFailure !== undefined && {isFailure: input.isFailure}),
              ...(input.notes !== undefined && {notes: input.notes}),
            },
          },
        ),
      );
      return ok(this.workoutMapper.transform(dto.workout));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async deleteSet(input: DeleteSetInput): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].exercises[':workoutExerciseId'].sets[
          ':setId'
        ].$delete({
          param: {
            workoutId: input.workoutId,
            workoutExerciseId: input.workoutExerciseId,
            setId: input.setId,
          },
        }),
      );
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async reorderExercises(
    input: ReorderWorkoutExercisesInput,
  ): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].exercises.order.$put({
          param: {workoutId: input.workoutId},
          json: {exerciseIds: [...input.exerciseIds]},
        }),
      );
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async removeExercise(
    input: RemoveExerciseFromWorkoutInput,
  ): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].exercises[':workoutExerciseId'].$delete({
          param: {
            workoutId: input.workoutId,
            workoutExerciseId: input.workoutExerciseId,
          },
        }),
      );
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async replaceExercise(
    input: ReplaceWorkoutExerciseInput,
  ): Promise<Result<ReplaceWorkoutExerciseResult, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].exercises[':workoutExerciseId'].replace.$post({
          param: {
            workoutId: input.workoutId,
            workoutExerciseId: input.workoutExerciseId,
          },
          json: {
            exerciseId: input.exerciseId,
            ...(input.restSeconds !== undefined && {restSeconds: input.restSeconds}),
            ...(input.targetSets !== undefined && {targetSets: input.targetSets}),
            ...(input.targetReps !== undefined && {targetReps: input.targetReps}),
            ...(input.targetWeightKg !== undefined && {targetWeightKg: input.targetWeightKg}),
          },
        }),
      );
      return ok(this.replaceMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async completeWorkout(
    workoutId: WorkoutId,
  ): Promise<Result<CompleteWorkoutResult, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].complete.$post({
          param: {workoutId},
        }),
      );
      return ok(this.completeMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  async addExercise(input: AddExerciseToWorkoutInput): Promise<Result<Workout, WorkoutError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.workouts[':workoutId'].exercises.$post({
          param: {workoutId: input.workoutId},
          json: {
            exerciseId: input.exerciseId,
            ...(input.restSeconds !== undefined && {restSeconds: input.restSeconds}),
            ...(input.targetSets !== undefined && {targetSets: input.targetSets}),
            ...(input.targetReps !== undefined && {targetReps: input.targetReps}),
            ...(input.targetWeightKg !== undefined && {
              targetWeightKg: input.targetWeightKg,
            }),
            ...(input.groupId !== undefined && {groupId: input.groupId}),
            ...(input.notes !== undefined && {notes: input.notes}),
          },
        }),
      );
      return ok(this.workoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'workout');
    }
  }

  private mapError(
    cause: unknown,
    resource: 'template' | 'workout' | 'range',
  ): Result<never, WorkoutError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 404) {
        return err(
          resource === 'template' ? new TemplateNotFoundError() : new WorkoutNotFoundError(),
        );
      }
      if (cause.statusCode === 409) {
        return err(new WorkoutAlreadyActiveError());
      }
      if (cause.statusCode === 400) {
        return err(
          resource === 'range' ? new InvalidDateRangeError() : new InvalidWorkoutInputError(),
        );
      }
      if (cause.statusCode === 422) {
        return err(new InvalidWorkoutInputError());
      }
      return err(new ServerError());
    }
    return err(new NetworkError());
  }
}
